import { ArrowLeft } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';

interface NotificationDetailHeaderProps {
  title?: string;
  status?: string;
  sentDate?: string;
  channel?: string;
  audience?: string;
  onBack: () => void;
}

/**
 * The heading over one send.
 *
 * Everything but the back button is optional so the same header stands while
 * the record is still loading — the reader keeps their way back out, and the
 * line under the title simply is not drawn until there is something true to put
 * in it. Nothing here has a placeholder standing in for real data.
 */
export function NotificationDetailHeader({
  title,
  status,
  sentDate,
  channel,
  audience,
  onBack,
}: NotificationDetailHeaderProps) {
  const meta = [sentDate && `Sent on ${sentDate}`, channel, audience].filter(Boolean) as string[];

  return (
    <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl  font-black tracking-tight text-slate-700 dark:text-white">
            {title ?? 'Notification'}
          </h1>
          {status && (
            <Badge
              tone={status === 'Sent' ? 'success' : status === 'Failed' ? 'critical' : 'warning'}
            >
              {status}
            </Badge>
          )}
        </div>
        {meta.length > 0 && (
          <p className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-2 flex-wrap">
            {meta.map((part, index) => (
              <span key={part} className="flex items-center gap-2">
                {index > 0 && <span>•</span>}
                <span>{part}</span>
              </span>
            ))}
          </p>
        )}
      </div>

      <Button
        variant="secondary"
        size="sm"
        onClick={onBack}
        className="text-[14px] font-extrabold border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0b1322] text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 shadow-xs transition-all cursor-pointer py-2 px-3.5 rounded-xl flex items-center gap-2 self-start sm:self-center"
      >
        <ArrowLeft className="h-4 w-4" />
        <span>Back to history</span>
      </Button>
    </header>
  );
}
